import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Portfolio.css";

const Portfolio = () => {
  // Projects data with website URLs and proper categories
  const projects = [
    {
      id: 1,
      title: "Venkateshwara Co-operative Power & Agro Processing Ltd.",
      image: "/projects/1.jpg",
      category: "Private",
      type: "Web Development",
      website: "https://www.venkateshwarapoweragro.com/"
    },
    {
      id: 2,
      title: "Godavari Nashik Municipal",
      image: "/projects/2.jpg",
      category: "Government",
      type: "Web Development",
      website: "https://godavari.nmc.gov.in/"
    },
    {
      id: 3,
      title: "AutoBranding",
      image: "/projects/3.jpg",
      category: "Private",
      type: "Web Development",
      website: "#"
    },
    {
      id: 4,
      title: "Our Godavari Website",
      image: "/projects/4.jpg",
      category: "Government",
      type: "Web Development",
      website: "https://ourgodavari.in/"
    },
    {
      id: 5,
      title: "Nashik Garden Management",
      image: "/projects/5.jpg",
      category: "Government",
      type: "Web Development",
      website: "https://garden.nmc.gov.in/"
    },
    {
      id: 6,
      title: "CKI Rubber Technologies (I) Private Ltd.",
      image: "/projects/6.jpg",
      category: "Private",
      type: "Web Development",
      website: "https://www.ckirubber.com/"
    },
    {
      id: 7,
      title: "Yuvasarathi Website",
      image: "/projects/7.jpg",
      category: "Private",
      type: "Web Development",
      website: "#"
    },
    {
      id: 8,
      title: "Ahilyanagar Festival Management System",
      image: "/projects/8.jpg",
      category: "Government",
      type: "Web Development",
      website: "https://amcfest.in/"
    },
    {
      id: 9,
      title: "Venkateshwara Krishak App",
      image: "/projects/9.png",
      category: "Private",
      type: "Android App",
      website: "#"
    },
    {
      id: 10,
      title: "Yuvasarathi App",
      image: "/projects/10.png",
      category: "Private",
      type: "Android App",
      website: "#"
    },
  ];

  // State for filtering projects
  const [activeFilter, setActiveFilter] = useState("All");
  const [filteredProjects, setFilteredProjects] = useState(projects);

  // Filter categories
  const filterCategories = ["All", "Government", "Private"];

  // Handle filter change
  const handleFilterChange = (category) => {
    setActiveFilter(category);
    if (category === "All") {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.category === category);
      setFilteredProjects(filtered);
    }
  };

  // Handle view button click
  const handleViewProject = (website) => {
    if (website !== "#") {
      window.open(website, '_blank');
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 16
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 20,
      transition: { duration: 0.25 }
    }
  };



  return (
    <div className="portfolio-main-section">
      {/* Background Blobs */}
      <div className="portfolio-bg-decorations">
        <div className="portfolio-decor-blob portfolio-decor-blob-1"></div>
        <div className="portfolio-decor-blob portfolio-decor-blob-2"></div>
      </div>

      <div className="container py-5 mt-5 position-relative" style={{ zIndex: 2 }}>
        {/* Header Title Section */}
        <div className="text-center mb-5 mt-4">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="portfolio-title"
          >
            OUR PROJECTS
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="portfolio-subtitle"
          >
            Explore our portfolio of successful projects that demonstrate our expertise and commitment to excellence.
          </motion.p>
        </div>

        {/* Liquid Tab Filters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="portfolio-tabs-container"
        >
          {filterCategories.map((category) => (
            <button
              key={category}
              className={`glass-filter-tab ${activeFilter === category ? "active" : ""}`}
              onClick={() => handleFilterChange(category)}
            >
              {activeFilter === category && (
                <motion.span
                  layoutId="activeFilterIndicator"
                  className="active-filter-indicator"
                  transition={{ type: "spring", stiffness: 350, damping: 28 }}
                />
              )}
              <span style={{ position: "relative", zIndex: 1 }}>{category} Projects</span>
            </button>
          ))}
        </motion.div>

        {/* Staggered Grid of Projects */}
        <motion.div
          layout
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="row g-4"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project) => (
              <motion.div
                layout
                key={project.id}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={() => handleViewProject(project.website)}
                className="col-12 col-sm-6 col-lg-4"
              >
                <div className="glass-portfolio-card">
                  {/* Category and Type Badges */}
                  <div className="portfolio-card-badges">
                    <span className={`p-badge ${project.category === "Government" ? "p-badge-govt" : "p-badge-pvt"}`}>
                      {project.category}
                    </span>
                    <span className="p-badge p-badge-type">
                      <i className={`bi ${project.type === "Android App" ? "bi-phone" : "bi-code-slash"} me-1`}></i>
                      {project.type}
                    </span>
                  </div>

                  {/* Image Container with Hover Overlay */}
                  <div className="portfolio-img-wrapper">
                    <img
                      src={project.image}
                      className="portfolio-img"
                      alt={project.title}
                    />

                    {/* Hover Overlay with View Trigger */}
                    <div className="portfolio-card-overlay">
                      <div className="portfolio-overlay-inner">
                        <button
                          disabled={project.website === "#"}
                          className="premium-view-btn"
                        >
                          <span>{project.website === "#" ? "Internal Project" : "View Website"}</span>
                          {project.website !== "#" && <i className="bi bi-box-arrow-up-right"></i>}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Card Title section */}
                  <div className="portfolio-card-body-section">
                    <h5 className="portfolio-card-title-text">{project.title}</h5>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* No Projects Found */}
        <AnimatePresence>
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="portfolio-empty-container mt-4"
            >
              <i className="bi bi-folder-x"></i>
              <h4>No Projects Found</h4>
              <p className="text-secondary mb-0">There are no projects matching the selected category.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Portfolio;
